<script setup lang="ts">
import { useUserStore } from '@/stores/user'
import { PersonIcon, PlusIcon } from '@radix-icons/vue'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Button } from './ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { CompanyRouteName } from '@/views/company/routes'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Permission } from '@/lib/permissions/v1'

const route = useRoute()
const router = useRouter()
const open = ref(false)
const userStore = useUserStore()
const { user } = storeToRefs(userStore)

function hasPermission(permission: string) {
    if (user.value.super_admin) {
        return true
    }

    return user.value.permissions.find((item) => item.name === permission) !== undefined
}

function goTo(name: string) {
    router.push({ name })
}
</script>

<template>
    <DropdownMenu v-model:open="open">
        <DropdownMenuTrigger as-child>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button
                            variant="icon"
                            size="icon"
                            :class="{ 'bg-accent': open, relative: true }"
                            @click="open = !open"
                        >
                            <PlusIcon />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                        <p>Create new...</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56" align="end">
            <DropdownMenuLabel class="flex text-xs">Create new</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
                v-if="hasPermission('ticket:create')"
                class="text-xs space-x-2"
                @click="
                    router.push({
                        query: { create: 'ticket', ...route.query },
                        params: route.params,
                    })
                "
            >
                <FontAwesomeIcon icon="fad fa-ticket" class="fa-fw" />
                <span>Ticket</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="hasPermission('article:create')"
                class="text-xs space-x-2"
                @click="goTo('articles.create')"
            >
                <FontAwesomeIcon icon="fad fa-newspaper" class="fa-fw" />
                <span>Article</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="hasPermission('company:create')"
                class="text-xs space-x-2"
                @click="goTo(CompanyRouteName.Create)"
            >
                <FontAwesomeIcon icon="fad fa-house" class="fa-fw" />
                <span>Company</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="hasPermission(Permission.CompanyContactCreate)"
                class="text-xs space-x-2"
                @click="
                    () => {
                        router.push({
                            query: { create: 'contact', ...route.query },
                        })
                    }
                "
            >
                <FontAwesomeIcon icon="fad fa-user" class="fa-fw" />
                <span>Contact</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="hasPermission('staff:create')"
                class="text-xs"
                @click="goTo('admin.staff.create')"
            >
                <PersonIcon class="mr-2" />
                Staff
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
